import React, { useState, useEffect, useRef } from 'react';
import { concatenatorAPI, handleAPIError } from '../api';
import { Button, Card, Metric, DataTable, Progress } from './ui';

const ConcatenatorModule = () => {
  // Estados principales para replicar exactamente Streamlit
  const [currentStep, setCurrentStep] = useState(0);
  const [logMessages, setLogMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Estado paso 0: Carga de archivos
  const [availableMarketplaces, setAvailableMarketplaces] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [baseData, setBaseData] = useState(null);
  const [baseMetadata, setBaseMetadata] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState({});
  
  // Estado paso 1: Configuración de procesamiento
  const [skipRowsConfig, setSkipRowsConfig] = useState({});
  const [processAmazon, setProcessAmazon] = useState(true);
  const [errorHandling, setErrorHandling] = useState('tolerant');
  const [columnNormalization, setColumnNormalization] = useState('basic');
  const [encoding, setEncoding] = useState('utf-8');
  
  // Estado paso 2: Limpieza de datos
  const [concatenatedData, setConcatenatedData] = useState(null);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [removeGhosts, setRemoveGhosts] = useState(false);
  const [ghostIdColumn, setGhostIdColumn] = useState('');
  const [ghostStatusColumn, setGhostStatusColumn] = useState('');
  const [ghostStrategy, setGhostStrategy] = useState('latest');
  const [priorityStatus, setPriorityStatus] = useState('');
  const [dropNaAll, setDropNaAll] = useState(false);
  const [dropNaCols, setDropNaCols] = useState([]);
  const [filterEnabled, setFilterEnabled] = useState(false);
  const [filterColumn, setFilterColumn] = useState('');
  const [filterText, setFilterText] = useState('');
  
  // Estado paso 3: Resultados
  const [cleanedData, setCleanedData] = useState(null);
  const [cleaningResults, setCleaningResults] = useState(null);
  
  // Preservar archivos originales para exportación completa
  const [originalFilesForExport, setOriginalFilesForExport] = useState([]);
  
  const progressLogRef = useRef(null);
  const fileInputRef = useRef(null);

  // Función para agregar mensajes al log con timestamp (como Streamlit)
  const addLogMessage = (message, clear = false) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `${timestamp} - ${message}`;
    
    setLogMessages(prev => {
      if (clear) return [logEntry];
      return [...prev, logEntry];
    });
    
    // Scroll al final del log
    setTimeout(() => {
      if (progressLogRef.current) {
        progressLogRef.current.scrollTop = progressLogRef.current.scrollHeight;
      }
    }, 100);
  };

  // Cargar marketplaces disponibles al montar el componente
  useEffect(() => {
    const loadAvailableMarketplaces = async () => {
      try {
        addLogMessage("Iniciando Módulo Concatenar con Base de Datos...", true);
        const response = await concatenatorAPI.getAvailableMarketplaces();
        if (response.success) {
          setAvailableMarketplaces(response.data);
          addLogMessage(`Encontrados ${response.data.length} concatenados disponibles`);
        }
      } catch (error) {
        console.error('Error loading marketplaces:', error);
        addLogMessage(`Error al cargar marketplaces: ${error.message}`);
      }
    };

    loadAvailableMarketplaces();
  }, []);

  // ==================== PASO 0: CARGAR ARCHIVOS ====================
  
  const loadBaseData = async (marketplace) => {
    try {
      setIsProcessing(true);
      addLogMessage(`Cargando concatenado base de ${marketplace.toUpperCase()}...`);
      
      const response = await concatenatorAPI.loadBaseData(marketplace);
      
      if (response.success) {
        setBaseData(response.data.dataframe);
        setBaseMetadata(response.data.metadata);
        setSelectedMarketplace(marketplace);
        
        // Mostrar el conteo REAL de la base de datos
        const totalReal = response.data.metadata.num_filas_total || response.data.dataframe.length;
        const muestra = response.data.dataframe.length;
        
        addLogMessage(`✅ Concatenado base cargado: ${totalReal.toLocaleString()} registros totales en BD (muestra de ${muestra.toLocaleString()}), ${Object.keys(response.data.dataframe[0] || {}).length} columnas`);
      } else {
        addLogMessage(`❌ Error al cargar concatenado base: ${response.message}`);
      }
    } catch (error) {
      const errorInfo = handleAPIError(error);
      addLogMessage(`❌ Error: ${errorInfo.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
    setOriginalFilesForExport(prev => [...prev, ...files]); // Preservar originales
    
    addLogMessage(`📁 ${files.length} archivo(s) subido(s):`);
    files.forEach((file, index) => {
      addLogMessage(`  ${index + 1}. ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    });
    
    // Generar previews para archivos CSV
    for (const file of files) {
      if (file.name.toLowerCase().endsWith('.csv')) {
        await generateFilePreview(file);
      }
    }
  };

  const generateFilePreview = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const lines = content.split('\n').slice(0, 4);
        const headers = lines[0]?.split(',') || [];
        const rows = lines.slice(1).map(line => line.split(','));
        
        setFilePreviews(prev => ({
          ...prev,
          [file.name]: {
            headers: headers.slice(0, 10),
            rows: rows.filter(row => row.length > 1).slice(0, 3),
            totalColumns: headers.length,
            estimatedRows: content.split('\n').length - 1
          }
        }));
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  const removeFile = (index) => {
    const file = uploadedFiles[index];
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setOriginalFilesForExport(prev => prev.filter((_, i) => i !== index)); // También de originales
    setFilePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[file.name];
      return newPreviews;
    });
    addLogMessage(`🗑️ Archivo eliminado: ${file.name}`);
  };

  // ==================== FUNCIÓN PRINCIPAL DE PROCESAMIENTO ====================
  
  const processFiles = async () => {
    try {
      setIsProcessing(true);
      addLogMessage("🚀 Iniciando procesamiento REAL...", false);
      
      let processedNewFiles = null;
      let newFilesTotalRecords = 0;
      let newFilesSampleSize = 0;
      let cleaningResultsFromAPI = null; // Capturar resultados de limpieza
      
      // PASO 1: PROCESAR ARCHIVOS NUEVOS (si hay)
      if (uploadedFiles.length > 0) {
        addLogMessage(`📁 Procesando ${uploadedFiles.length} archivos nuevos con API...`);
        
        try {
          // Crear FormData para enviar archivos al backend
          const formData = new FormData();
          uploadedFiles.forEach((file, index) => {
            const skipRows = skipRowsConfig[file.name] || 0;
            const sizeKB = (file.size / 1024).toFixed(2);
            const sizeMB = (file.size / 1024 / 1024).toFixed(2);
            const sizeStr = file.size > 1024 * 1024 ? `${sizeMB} MB` : `${sizeKB} KB`;
            addLogMessage(`  📄 ${index + 1}. ${file.name} (${sizeStr}, saltando ${skipRows} filas)`);
            formData.append('files', file);
            formData.append(`skip_rows_${index}`, skipRows.toString());
          });
          
          // ✅ ENVIAR OPCIONES DE LIMPIEZA AL BACKEND
          formData.append('removeDuplicates', removeDuplicates.toString());
          formData.append('removeGhosts', removeGhosts.toString());
          formData.append('ghostIdColumn', ghostIdColumn || '');
          formData.append('ghostStatusColumn', ghostStatusColumn || '');
          formData.append('ghostStrategy', ghostStrategy || 'latest');
          formData.append('priorityStatus', priorityStatus || '');
          formData.append('dropNaAll', dropNaAll.toString());
          formData.append('dropNaCols', JSON.stringify(dropNaCols));
          formData.append('filterEnabled', filterEnabled.toString());
          formData.append('filterColumn', filterColumn || '');
          formData.append('filterText', filterText || '');
          
          // Agregar configuraciones de procesamiento
          formData.append('process_amazon', processAmazon.toString());
          formData.append('error_handling', errorHandling);
          formData.append('column_normalization', columnNormalization);
          formData.append('encoding', encoding);
          
          addLogMessage("⏳ Procesando archivos en servidor...");
          if (removeDuplicates || removeGhosts) {
            addLogMessage(`🧹 Opciones de limpieza activadas:`);
            if (removeDuplicates) addLogMessage(`   • Eliminar duplicados: SÍ`);
            if (removeGhosts) addLogMessage(`   • Eliminar fantasmas: SÍ (ID: ${ghostIdColumn}, Estado: ${ghostStatusColumn})`);
          }
          
          // Llamar al endpoint real de procesamiento
          const response = await concatenatorAPI.processFiles(formData);
          
          if (response.success && response.data) {
            processedNewFiles = response.data;
            const metadata = response.metadata || {};
            newFilesTotalRecords = metadata.total_records || processedNewFiles.length || 0;
            newFilesSampleSize = metadata.sample_size || processedNewFiles.length || 0;
            const processingMode = metadata.processing_mode || 'unknown';
            cleaningResultsFromAPI = metadata.cleaning_results || null; // Capturar para uso posterior
            
            // Mostrar información detallada de cada archivo procesado
            if (metadata.files_info && metadata.files_info.length > 0) {
              const filesLoaded = metadata.files_loaded || metadata.files_info.filter(f => f.status !== false).length;
              const filesFailed = metadata.files_failed || 0;
              
              addLogMessage(`📊 Detalles de archivos (${filesLoaded}/${metadata.files_info.length} exitosos):`);
              metadata.files_info.forEach((fileInfo, idx) => {
                const sizeMB = fileInfo.size_mb ? fileInfo.size_mb.toFixed(2) : '?';
                const rows = fileInfo.rows ? fileInfo.rows.toLocaleString() : '?';
                const cols = fileInfo.columns || '?';
                const status = fileInfo.status === false ? '❌ ERROR' : '✅';
                const error = fileInfo.error ? ` (${fileInfo.error})` : '';
                addLogMessage(`   ${status} ${idx + 1}. ${fileInfo.name}: ${rows} filas × ${cols} cols (${sizeMB} MB)${error}`);
              });
              
              if (filesFailed > 0) {
                addLogMessage(`⚠️ ADVERTENCIA: ${filesFailed} archivos fallaron al cargar`);
              }
              
              if (metadata.loading_errors && metadata.loading_errors.length > 0) {
                addLogMessage(`❌ Errores de carga:`);
                metadata.loading_errors.forEach(err => {
                  addLogMessage(`   • ${err}`);
                });
              }
            }
            
            addLogMessage(`✅ Archivos procesados: ${newFilesTotalRecords.toLocaleString()} registros totales`);
            addLogMessage(`📊 Muestra cargada en memoria: ${newFilesSampleSize.toLocaleString()} registros`);
            
            // Mostrar resultados de limpieza si se aplicó
            if (metadata.cleaning_applied && metadata.cleaning_results) {
              const cleaningRes = metadata.cleaning_results;
              const beforeCleaning = metadata.records_before_cleaning || newFilesTotalRecords;
              const removed = beforeCleaning - newFilesTotalRecords;
              
              if (removed > 0) {
                addLogMessage(`🧹 Limpieza aplicada en el servidor:`);
                addLogMessage(`   • Registros antes: ${beforeCleaning.toLocaleString()}`);
                addLogMessage(`   • Registros después: ${newFilesTotalRecords.toLocaleString()}`);
                addLogMessage(`   • Total eliminado: ${removed.toLocaleString()} (${((removed/beforeCleaning)*100).toFixed(1)}%)`);
                if (cleaningRes.duplicates_removed > 0) {
                  addLogMessage(`   • Duplicados eliminados: ${cleaningRes.duplicates_removed.toLocaleString()}`);
                }
                if (cleaningRes.ghost_removed > 0) {
                  addLogMessage(`   • Fantasmas eliminados: ${cleaningRes.ghost_removed.toLocaleString()}`);
                }
              } else {
                addLogMessage(`✓ Limpieza aplicada: No se encontraron registros para eliminar (datos ya limpios)`);
              }
            }
          } else {
            throw new Error(response.message || 'Error procesando archivos');
          }
        } catch (error) {
          addLogMessage(`❌ Error procesando archivos nuevos: ${error.message}`);
          console.error('Error en processFiles:', error);
          throw error; // Propagar error para no continuar con datos inválidos
        }
      }
      
      // PASO 2: COMBINAR CON BASE DE DATOS (si hay)
      if (baseData && processedNewFiles) {
        // Caso: baseData + archivos nuevos procesados
        addLogMessage("🔄 Combinando datos base con archivos nuevos procesados...");
        
        const totalRealBD = baseMetadata?.num_filas_total || baseData.length;
        const totalCombinado = totalRealBD + newFilesTotalRecords;
        
        // Concatenar las muestras en memoria
        const combinedSample = [...baseData, ...processedNewFiles];
        
        addLogMessage(`✅ Datos combinados REALES:`);
        addLogMessage(`   • Base de datos: ${totalRealBD.toLocaleString()} registros`);
        addLogMessage(`   • Archivos nuevos: ${newFilesTotalRecords.toLocaleString()} registros`);
        addLogMessage(`   • TOTAL COMBINADO: ${totalCombinado.toLocaleString()} registros`);
        addLogMessage(`   • Muestra en memoria: ${combinedSample.length.toLocaleString()} registros`);
          
          setConcatenatedData({
          data: combinedSample, // Muestra combinada en memoria
          totalRecords: totalCombinado, // Total real combinado
          totalRealBD: totalRealBD, // Total original en BD
          sampleSize: combinedSample.length, // Tamaño de muestra trabajando
          processingMode: 'combined',
          columns: combinedSample.length > 0 ? Object.keys(combinedSample[0]).length : 0,
          cleaningResults: cleaningResultsFromAPI // Resultados de limpieza del backend
        });
      } else if (baseData && !processedNewFiles) {
        // Caso: Solo baseData, sin archivos nuevos
        addLogMessage("📊 Trabajando solo con datos base (sin archivos nuevos)...");
        
        const totalRealBD = baseMetadata?.num_filas_total || baseData.length;
        
        addLogMessage(`   • Total en BD: ${totalRealBD.toLocaleString()} registros`);
        addLogMessage(`   • Muestra en memoria: ${baseData.length.toLocaleString()} registros`);
        addLogMessage(`   ⚠️ IMPORTANTE: La limpieza solo trabajará con la muestra en memoria`);
          
          setConcatenatedData({
          data: baseData,
          totalRecords: totalRealBD,
          totalRealBD: totalRealBD,
          sampleSize: baseData.length,
          processingMode: 'database_only',
          columns: Object.keys(baseData[0] || {}).length,
          cleaningResults: null // No hay limpieza en este caso
        });
      } else if (processedNewFiles && !baseData) {
        // Caso: Solo archivos nuevos, sin baseData
        addLogMessage("📁 Trabajando solo con archivos nuevos (sin base de datos)...");
        
        addLogMessage(`   • Total procesado: ${newFilesTotalRecords.toLocaleString()} registros`);
        addLogMessage(`   • Muestra en memoria: ${newFilesSampleSize.toLocaleString()} registros`);
        
        setConcatenatedData({
          data: processedNewFiles,
          totalRecords: newFilesTotalRecords,
          sampleSize: newFilesSampleSize,
          processingMode: 'files_only',
          columns: processedNewFiles.length > 0 ? Object.keys(processedNewFiles[0]).length : 0,
          cleaningResults: cleaningResultsFromAPI // Resultados de limpieza del backend
        });
      } else {
        throw new Error('No hay datos para procesar. Carga una base de datos o sube archivos.');
      }
      
      addLogMessage("🎉 Procesamiento REAL completado exitosamente!");
      setCurrentStep(2);
      
    } catch (error) {
      addLogMessage(`❌ Error durante el procesamiento: ${error.message}`);
      console.error('Error en processFiles:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Función auxiliar para normalizar filas y detectar duplicados correctamente
  const normalizeRowForComparison = (row) => {
    // Ordenar las claves alfabéticamente para comparación consistente
    // Esto soluciona el problema de que JSON.stringify genera diferentes strings
    // cuando las propiedades están en diferente orden
    const orderedKeys = Object.keys(row).sort();
    const orderedObj = {};
    orderedKeys.forEach(key => {
      orderedObj[key] = row[key];
    });
    // Nota: JSON.stringify maneja automáticamente la precisión de números grandes
    // Si dos números son considerados iguales por JavaScript (debido a limitaciones
    // de precisión), serán tratados como iguales en la comparación
    return JSON.stringify(orderedObj);
  };

  const applyCleaning = async () => {
    try {
      setIsProcessing(true);
      
      // ✅ LA LIMPIEZA YA SE APLICÓ EN EL BACKEND durante processFiles
      // Este paso ahora solo prepara los datos para exportación
      addLogMessage("✓ La limpieza ya fue aplicada por el servidor durante el procesamiento", false);
      
      // Validar que hay datos
      if (!concatenatedData || !concatenatedData.data || concatenatedData.data.length === 0) {
        addLogMessage("❌ No hay datos para exportar");
        setIsProcessing(false);
        return;
      }
      
      // Los datos ya vienen limpios del backend, solo pasamos al siguiente paso
      const cleanedDataArray = concatenatedData.data;
      
      addLogMessage(`📊 Preparando ${cleanedDataArray.length.toLocaleString()} registros limpios para exportación...`);
      
      // Los resultados de limpieza ya vienen del backend
      const cleaningResultsFromBackend = concatenatedData.cleaningResults || {
        duplicatesRemoved: 0,
        ghostsRemoved: 0,
        totalRemoved: 0,
        percentRemoved: 0
      };
      
      setCleanedData({
        data: cleanedDataArray, // DATOS YA LIMPIOS POR EL BACKEND
        totalRecords: concatenatedData.totalRecords,
        totalRealBD: concatenatedData.totalRealBD,
        sampleSize: cleanedDataArray.length,
        processingMode: concatenatedData.processingMode,
        columns: cleanedDataArray.length > 0 ? Object.keys(cleanedDataArray[0]).length : 0
      });
      
      setCleaningResults(cleaningResultsFromBackend);
      
      setCurrentStep(3);
      
    } catch (error) {
      addLogMessage(`❌ Error durante la limpieza: ${error.message}`);
      console.error('Error en applyCleaning:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetModule = () => {
    setCurrentStep(0);
    setSelectedMarketplace(null);
    setBaseData(null);
    setBaseMetadata(null);
    setUploadedFiles([]);
    setOriginalFilesForExport([]); // Limpiar archivos originales
    setFilePreviews({});
    setSkipRowsConfig({});
    setConcatenatedData(null);
    setCleanedData(null);
    setCleaningResults(null);
    // Reiniciar opciones de limpieza
    setRemoveDuplicates(false);
    setRemoveGhosts(false);
    setGhostIdColumn('');
    setGhostStatusColumn('');
    setGhostStrategy('latest');
    setPriorityStatus('');
    setDropNaAll(false);
    setDropNaCols([]);
    setFilterEnabled(false);
    setFilterColumn('');
    setFilterText('');
    setLogMessages([]);
    addLogMessage("🔄 Módulo reiniciado completamente", true);
  };

  // ==================== RENDERIZADO DE PASOS ====================

  const renderStep0 = () => (
    <div className="step-content">
      <h3>Paso 0: Cargar concatenado base desde BD y archivos nuevos para agregar</h3>
      
      {/* Opción 1: Cargar concatenado base desde Base de Datos */}
      <Card className="section-card">
        <h4>📊 Opción 1: Cargar concatenado base desde Base de Datos</h4>
        
        {availableMarketplaces.length > 0 ? (
          <div className="marketplace-selection">
            <p>Concatenados disponibles en la base de datos:</p>
            
            <div className="marketplace-grid">
              {availableMarketplaces.map((marketplace) => (
                <div key={marketplace.name} className="marketplace-item">
                  <div className="marketplace-header">
                    <h5>📈 {marketplace.name.toUpperCase()}</h5>
                    <span className="marketplace-count">{marketplace.num_filas?.toLocaleString() || '0'} registros</span>
                  </div>
                  <div className="marketplace-details">
                    <div><strong>Fecha:</strong> {marketplace.fecha}</div>
                    <div><strong>Tabla:</strong> {marketplace.tabla_actual}</div>
                    <div><strong>Origen:</strong> {marketplace.archivo_original || 'Múltiples archivos'}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="selection-controls">
              <select 
                value={selectedMarketplace || ''}
                onChange={(e) => setSelectedMarketplace(e.target.value || null)}
                className="marketplace-select"
              >
                <option value="">Ninguno</option>
                {availableMarketplaces.map(mp => (
                  <option key={mp.name} value={mp.name}>{mp.name.toUpperCase()}</option>
                ))}
              </select>
              
              {selectedMarketplace && (
                <Button
                  variant="primary"
                  onClick={() => loadBaseData(selectedMarketplace)}
                  disabled={isProcessing}
                  loading={isProcessing}
                >
                  {isProcessing ? 'Cargando...' : `Cargar ${selectedMarketplace.toUpperCase()}`}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p>No se encontraron concatenados en la base de datos.</p>
        )}
        
        {baseData && baseMetadata && (
            <div className="loaded-base-info">
              <h5>✅ Concatenado Base Cargado</h5>
              <div className="base-metrics">
                <Metric label="Marketplace" value={selectedMarketplace?.toUpperCase() || 'N/A'} />
                <Metric label="Registros (Total BD)" value={baseMetadata?.num_filas_total?.toLocaleString() || 'N/A'} />
                <Metric label="Muestra Cargada" value={baseData.length?.toLocaleString() || '0'} />
                <Metric label="Columnas" value={baseData[0] ? Object.keys(baseData[0]).length : '0'} />
                <Metric label="Fecha" value={baseMetadata?.fecha || 'N/A'} />
              </div>
            
            <details className="preview-details">
              <summary>Ver muestra de datos (primeras 3 filas)</summary>
              <div className="data-preview">
                {baseData.slice(0, 3).map((row, index) => (
                  <div key={index} className="preview-row">
                    <strong>#{index + 1}</strong>
                    {Object.entries(row).slice(0, 5).map(([key, value]) => (
                      <span key={key}>
                        <strong>{key}:</strong> {String(value).substring(0, 50)}
                        {String(value).length > 50 ? '...' : ''}
                      </span>
                    ))}
                    {Object.keys(row).length > 5 && <span>+{Object.keys(row).length - 5} columnas más</span>}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </Card>

      {/* Opción 2: Archivos nuevos para agregar */}
      <Card className="section-card">
        <h4>📁 Opción 2: Archivos nuevos para agregar</h4>
        
        {baseData && (
          <div className="base-info">
            🎯 <strong>Base actual:</strong> {selectedMarketplace?.toUpperCase()} con {baseData.length?.toLocaleString()} registros
          </div>
        )}
        
        <p>Sube archivos (CSV, XLS, XLSX) que se agregarán al concatenado base. Los duplicados se detectan si todas las columnas tienen valores idénticos.</p>
        
        <div 
          className="upload-area"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xls,.xlsx"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <div className="upload-content">
            <div className="upload-icon">📁</div>
            <p>Haz clic aquí para seleccionar archivos CSV o Excel</p>
            <p className="upload-subtitle">Soporta archivos .csv, .xlsx, .xls</p>
          </div>
        </div>
        
        {uploadedFiles.length > 0 && (
          <div className="files-list">
            <h5>Archivos Seleccionados ({uploadedFiles.length})</h5>
            {uploadedFiles.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-info">
                  <strong>{file.name}</strong>
                  <span>({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                  <span>{new Date(file.lastModified).toLocaleDateString()}</span>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => removeFile(index)}
                  className="file-remove"
                >
                  🗑️ Eliminar
                </Button>
                
                {/* Vista previa si está disponible */}
                {filePreviews[file.name] && (
                  <details className="file-preview">
                    <summary>Vista previa</summary>
                    <div className="preview-info">
                      <span>~{filePreviews[file.name].estimatedRows} filas</span>
                      <span>{filePreviews[file.name].totalColumns} columnas</span>
                    </div>
                    <div className="preview-table">
                      <div className="preview-headers">
                        {filePreviews[file.name].headers.map((header, i) => (
                          <div key={i} className="preview-cell header">{header || `Col ${i + 1}`}</div>
                        ))}
                        {filePreviews[file.name].totalColumns > 10 && (
                          <div className="preview-cell more">+{filePreviews[file.name].totalColumns - 10} más</div>
                        )}
                      </div>
                      {filePreviews[file.name].rows.slice(0, 3).map((row, i) => (
                        <div key={i} className="preview-row">
                          {row.slice(0, 10).map((cell, j) => (
                            <div key={j} className="preview-cell">
                              {String(cell).substring(0, 30)}
                              {String(cell).length > 30 ? '...' : ''}
                            </div>
                          ))}
                          {row.length > 10 && <div className="preview-cell more">...</div>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="step-actions">
        {(baseData || uploadedFiles.length > 0) ? (
          <div className="ready-message">
            ✅ Listo: {baseData ? 'Concatenado base' : ''}{baseData && uploadedFiles.length > 0 ? ' + ' : ''}{uploadedFiles.length > 0 ? 'archivos nuevos' : ''}
          </div>
        ) : (
          <div className="warning-message">
            ⚠️ Necesitas al menos un concatenado base o archivos nuevos para continuar.
          </div>
        )}
        
        <Button
          variant="primary"
          onClick={() => setCurrentStep(1)}
          disabled={!baseData && uploadedFiles.length === 0}
        >
          Continuar al procesamiento
        </Button>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="step-content">
      <h3>Paso 1: Configurar opciones de procesamiento y concatenar</h3>
      
      {/* Resumen de datos a procesar */}
      <Card className="section-card">
        <h4>🔍 Resumen de datos a procesar</h4>
        
        {baseData && (
          <div className="data-summary">
            <strong>📊 Concatenado base:</strong> {selectedMarketplace?.toUpperCase()} - {baseMetadata?.num_filas_total?.toLocaleString() || baseData.length?.toLocaleString()} registros totales en BD (muestra: {baseData.length?.toLocaleString()}), {Object.keys(baseData[0] || {}).length} columnas
          </div>
        )}
        
        {uploadedFiles.length > 0 && (
          <div className="data-summary">
            <strong>📁 Archivos nuevos:</strong> {uploadedFiles.length} archivos
            {uploadedFiles.map((file, index) => (
              <div key={index} className="file-summary">
                {index + 1}. {file.name}
              </div>
            ))}
          </div>
        )}
        
        <div className="processing-mode">
          <strong>🔄 Modo:</strong> {baseData && uploadedFiles.length > 0 
            ? 'Combinación (concatenado base + archivos nuevos)'
            : baseData 
              ? 'Solo concatenado base (sin archivos nuevos)'
              : 'Solo archivos nuevos (concatenador tradicional)'
          }
        </div>
      </Card>

      {/* Configuración por archivo */}
      {uploadedFiles.length > 0 && (
        <Card className="section-card">
          <h4>📋 Configuración por archivo</h4>
          <p>Configura cuántas filas saltar desde el inicio para cada archivo (encabezados, filas de metadatos, etc.)</p>
          
          <div className="file-config-list">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="file-config-item">
                <div className="file-info">
                  <strong>{index + 1}. {file.name}</strong>
                  <span>({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
                <div className="skip-rows-control">
                  <label>Filas a saltar:</label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={skipRowsConfig[file.name] || 0}
                    onChange={(e) => setSkipRowsConfig(prev => ({
                      ...prev,
                      [file.name]: parseInt(e.target.value) || 0
                    }))}
                    className="skip-rows-input"
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Opciones avanzadas */}
      <Card className="section-card">
        <details className="advanced-options">
          <summary><h4>⚙️ Opciones avanzadas</h4></summary>
          
          <div className="option-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={processAmazon}
                onChange={(e) => setProcessAmazon(e.target.checked)}
              />
              Procesar automáticamente archivos de Amazon
            </label>
          </div>
          
          <div className="option-group">
            <label>Manejo de errores:</label>
            <div className="radio-group">
              {[
                { value: 'strict', label: 'Estricto (detener en cualquier error)' },
                { value: 'tolerant', label: 'Tolerante (ignorar archivos con errores)' },
                { value: 'manual', label: 'Manual (preguntar qué hacer en cada error)' }
              ].map(option => (
                <label key={option.value} className="radio-label">
                  <input
                    type="radio"
                    name="errorHandling"
                    value={option.value}
                    checked={errorHandling === option.value}
                    onChange={(e) => setErrorHandling(e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
          
          <div className="option-group">
            <label>Normalización de columnas:</label>
            <div className="radio-group">
              {[
                { value: 'basic', label: 'Básica (strip + lowercase)' },
                { value: 'advanced', label: 'Avanzada (eliminar caracteres especiales)' }
              ].map(option => (
                <label key={option.value} className="radio-label">
                  <input
                    type="radio"
                    name="columnNormalization"
                    value={option.value}
                    checked={columnNormalization === option.value}
                    onChange={(e) => setColumnNormalization(e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
          
          <div className="option-group">
            <label>Codificación de archivos CSV:</label>
            <select 
              value={encoding}
              onChange={(e) => setEncoding(e.target.value)}
              className="encoding-select"
            >
              <option value="utf-8">utf-8</option>
              <option value="latin1">latin1</option>
              <option value="cp1252">cp1252</option>
              <option value="ISO-8859-1">ISO-8859-1</option>
            </select>
          </div>
        </details>
      </Card>

      {/* Opciones de limpieza (ahora en paso 1) */}
      {uploadedFiles.length > 0 && (
        <Card className="section-card">
          <h4>🧹 Opciones de limpieza (aplicadas durante el procesamiento)</h4>
          <p>Estas opciones se aplicarán en el servidor cuando proceses los archivos</p>
            
            <div className="option-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={removeDuplicates}
                  onChange={(e) => setRemoveDuplicates(e.target.checked)}
                />
                Eliminar registros duplicados
              </label>
            </div>
            
            <div className="option-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={removeGhosts}
                  onChange={(e) => setRemoveGhosts(e.target.checked)}
                />
                Eliminar registros fantasma (mismo ID, diferente estado)
              </label>
              
              {removeGhosts && (
                <div className="ghost-config">
                <p style={{ fontSize: '14px', color: '#4A5568', marginBottom: '16px' }}>
                  Los registros fantasma son duplicados con el mismo ID pero diferente estado. Configura cómo manejarlos:
                </p>
                
                {/* Guía rápida de defaults */}
                <div style={{ 
                  padding: '12px', 
                  background: 'rgba(41, 181, 232, 0.08)', 
                  border: '2px solid #29B5E8', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  fontSize: '13px'
                }}>
                  <strong>💡 Valores por defecto recomendados:</strong>
                  <div style={{ marginTop: '8px', lineHeight: '1.8' }}>
                    • <strong>Amazon:</strong> ID = <code>amazon-order-id</code>, Estado = <code>order-status</code>
                    <br />
                    • <strong>MercadoLibre:</strong> ID = <code># de venta</code>, Estado = <code>status</code> o <code>estado</code>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <button 
                      onClick={() => {
                        setGhostIdColumn('amazon-order-id');
                        setGhostStatusColumn('order-status');
                      }}
                      style={{
                        padding: '4px 12px',
                        marginRight: '8px',
                        background: '#29B5E8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      Usar valores Amazon
                    </button>
                    <button 
                      onClick={() => {
                        setGhostIdColumn('# de venta');
                        setGhostStatusColumn('status');
                      }}
                      style={{
                        padding: '4px 12px',
                        background: '#FFD700',
                        color: '#1A202C',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      Usar valores MercadoLibre
                    </button>
                  </div>
                </div>
                
                  <div className="ghost-selectors">
                    <div className="selector-group">
                      <label>Columna de ID del pedido:</label>
                    <input
                      type="text"
                        value={ghostIdColumn}
                        onChange={(e) => setGhostIdColumn(e.target.value)}
                      placeholder="ej: amazon-order-id, # de venta"
                        className="ghost-select"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <small style={{ fontSize: '12px', color: '#4A5568', marginTop: '4px', display: 'block' }}>
                      Escribe el nombre exacto de la columna (sensible a mayúsculas)
                    </small>
                    </div>
                    
                    <div className="selector-group">
                      <label>Columna de estado:</label>
                    <input
                      type="text"
                        value={ghostStatusColumn}
                        onChange={(e) => setGhostStatusColumn(e.target.value)}
                      placeholder="ej: order-status, status, estado"
                        className="ghost-select"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <small style={{ fontSize: '12px', color: '#4A5568', marginTop: '4px', display: 'block' }}>
                      Escribe el nombre exacto de la columna (sensible a mayúsculas)
                    </small>
                    </div>
                  </div>
                  
                  <div className="strategy-group">
                  <label>Estrategia:</label>
                    <div className="radio-group">
                      {[
                        { value: 'latest', label: 'Mantener el más reciente' },
                        { value: 'first', label: 'Mantener el primer registro' },
                        { value: 'priority', label: 'Especificar estado prioritario' }
                      ].map(option => (
                        <label key={option.value} className="radio-label">
                          <input
                            type="radio"
                            name="ghostStrategy"
                            value={option.value}
                            checked={ghostStrategy === option.value}
                            onChange={(e) => setGhostStrategy(e.target.value)}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                {ghostStrategy === 'priority' && (
                    <div className="priority-group">
                    <label>Estado prioritario:</label>
                    <input
                      type="text"
                        value={priorityStatus}
                        onChange={(e) => setPriorityStatus(e.target.value)}
                      placeholder="ej: Shipped, Delivered, Cancelled"
                        className="priority-select"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <small style={{ fontSize: '12px', color: '#4A5568', marginTop: '4px', display: 'block' }}>
                      Estados comunes: Shipped, Delivered, Cancelled, Pending
                    </small>
                    </div>
                  )}
                </div>
              )}
            </div>
        </Card>
      )}

      <div className="step-actions">
        <Button
          variant="primary"
          onClick={processFiles}
          disabled={isProcessing || (!baseData && uploadedFiles.length === 0)}
          loading={isProcessing}
          size="large"
        >
          {isProcessing ? 'Procesando...' : '🚀 Procesar, concatenar y limpiar datos'}
        </Button>
            </div>
    </div>
  );

  const renderStep2 = () => {
    if (!concatenatedData) {
      return (
        <div className="step-content">
          <p>❌ No se encontró el DataFrame concatenado. Vuelve al paso 1.</p>
          <Button variant="secondary" onClick={() => setCurrentStep(1)}>
            ← Volver al paso 1
          </Button>
              </div>
      );
    }

    return (
      <div className="step-content">
        <h3>Paso 2: Revisión de datos procesados</h3>
        
        <div className="data-info">
          <strong>📊 Datos concatenados:</strong>
          <div style={{ marginTop: '12px' }}>
            {concatenatedData.processingMode === 'combined' && (
              <>
                <div>• Base de datos: {concatenatedData.totalRealBD?.toLocaleString()} registros</div>
                <div>• Archivos nuevos: {(concatenatedData.totalRecords - concatenatedData.totalRealBD).toLocaleString()} registros</div>
                <div><strong>• TOTAL COMBINADO: {concatenatedData.totalRecords.toLocaleString()} registros</strong></div>
                <div>• Muestra en memoria trabajando: {concatenatedData.data.length.toLocaleString()} registros</div>
              </>
            )}
            {concatenatedData.processingMode === 'database_only' && (
              <>
                <div>• Total en BD: {concatenatedData.totalRecords.toLocaleString()} registros</div>
                <div>• Muestra en memoria trabajando: {concatenatedData.data.length.toLocaleString()} registros</div>
              </>
            )}
            {concatenatedData.processingMode === 'files_only' && (
              <>
                <div>• Total en archivos: {concatenatedData.totalRecords.toLocaleString()} registros</div>
                <div>• Muestra en memoria trabajando: {concatenatedData.data.length.toLocaleString()} registros</div>
              </>
            )}
            <div>• Columnas: {concatenatedData.columns}</div>
                  </div>
                  
          {concatenatedData.data.length < concatenatedData.totalRecords && (
            <div className="sample-warning" style={{ marginTop: '16px', padding: '12px', background: 'rgba(255, 107, 53, 0.1)', border: '2px solid #FF6B35', borderRadius: '8px' }}>
              <strong>⚠️ IMPORTANTE: Trabajando con muestra</strong>
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                • La limpieza se aplicará SOLO a los {concatenatedData.data.length.toLocaleString()} registros en memoria
                <br />
                • Los duplicados fuera de esta muestra NO serán detectados
                <br />
                {originalFilesForExport.length > 0 && (
                  <>• Para limpieza completa, usa "Descargar CSV Completo" en el paso 3 (procesará todos los datos)</>
                )}
              </div>
                    </div>
                  )}
                </div>

        <Card className="section-card">
          <h4>✅ Datos procesados y limpios</h4>
          <p>Los datos fueron procesados y limpiados en el servidor. Revisa el resumen y continúa a la exportación.</p>
          
          {concatenatedData.cleaningResults && (
            <div className="cleaning-summary" style={{ marginTop: '20px', padding: '20px', background: 'rgba(56, 193, 114, 0.08)', border: '2px solid #38C172', borderRadius: '12px' }}>
              <h5 style={{ fontSize: '18px', fontWeight: '700', color: '#1A202C', marginBottom: '16px' }}>🧹 Limpieza aplicada por el servidor:</h5>
              <div style={{ lineHeight: '2' }}>
                {concatenatedData.cleaningResults.duplicates_removed > 0 && (
                  <div>• Duplicados eliminados: {concatenatedData.cleaningResults.duplicates_removed.toLocaleString()}</div>
                )}
                {concatenatedData.cleaningResults.ghost_removed > 0 && (
                  <div>• Registros fantasma eliminados: {concatenatedData.cleaningResults.ghost_removed.toLocaleString()}</div>
                )}
                {concatenatedData.cleaningResults.duplicates_removed === 0 && concatenatedData.cleaningResults.ghost_removed === 0 && (
                  <div>✓ No se encontraron registros para eliminar - Los datos ya están limpios</div>
              )}
            </div>
            </div>
          )}
          
          <div className="info-message" style={{ marginTop: '20px' }}>
            ℹ️ <strong>Nota:</strong> Si necesitas cambiar las opciones de limpieza, regresa al Paso 1.
          </div>
        </Card>

        <div className="step-actions">
          <Button
            variant="primary"
            onClick={applyCleaning}
            disabled={isProcessing}
            loading={isProcessing}
            size="large"
          >
            {isProcessing ? 'Preparando...' : '➡️ Continuar a Exportación'}
          </Button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    if (!cleanedData) {
      return (
        <div className="step-content">
          <p>❌ No se encontraron datos procesados. Vuelve al paso anterior.</p>
          <Button variant="secondary" onClick={() => setCurrentStep(2)}>
            ← Volver al paso 2
          </Button>
        </div>
      );
    }

    const saveToDatabase = async () => {
      try {
        setIsProcessing(true);
        addLogMessage("💾 Guardando datos LIMPIOS en base de datos...", false);
        
        // Validar que tenemos datos para guardar
        if (!cleanedData || !cleanedData.data || cleanedData.data.length === 0) {
          throw new Error('No hay datos limpios para guardar');
        }
        
        // Preparar datos para guardar
        const saveData = {
          marketplace: selectedMarketplace || 'custom',
          data: cleanedData.data, // Datos YA limpios
          metadata: {
            total_records: cleanedData.totalRecords,
            sample_size: cleanedData.sampleSize || cleanedData.data.length,
            columns: cleanedData.columns,
            processing_mode: cleanedData.processingMode,
            cleaned: true,
            cleaning_applied: {
              duplicatesRemoved: cleaningResults?.duplicatesRemoved || 0,
              ghostsRemoved: cleaningResults?.ghostsRemoved || 0,
              naAllRemoved: cleaningResults?.naAllRemoved || 0,
              naColsRemoved: cleaningResults?.naColsRemoved || 0,
              filterRemoved: cleaningResults?.filterRemoved || 0,
              totalRemoved: cleaningResults?.totalRemoved || 0,
              percentRemoved: cleaningResults?.percentRemoved || 0
            },
            cleaning_config: {
              removeDuplicates,
              removeGhosts: removeGhosts && ghostIdColumn && ghostStatusColumn,
              ghostIdColumn: removeGhosts ? ghostIdColumn : null,
              ghostStatusColumn: removeGhosts ? ghostStatusColumn : null,
              ghostStrategy: removeGhosts ? ghostStrategy : null,
              dropNaAll,
              dropNaCols: dropNaCols.length > 0 ? dropNaCols : null,
              filterEnabled,
              filterColumn: filterEnabled ? filterColumn : null,
              filterText: filterEnabled ? filterText : null
            },
            timestamp: new Date().toISOString()
          }
        };
        
        addLogMessage(`📤 Enviando ${cleanedData.data.length.toLocaleString()} registros limpios al servidor...`);
        addLogMessage(`🧹 Limpieza aplicada: ${cleaningResults?.totalRemoved?.toLocaleString() || 0} registros eliminados`);
        
        // Llamar al endpoint de guardado
        const response = await concatenatorAPI.saveToDatabase(saveData);
        
        if (response.success) {
          addLogMessage(`✅ ${cleanedData.totalRecords.toLocaleString()} registros limpios guardados exitosamente`);
          addLogMessage(`📊 Versión actualizada creada para ${selectedMarketplace?.toUpperCase() || 'concatenado personalizado'}`);
          alert('🎉 Datos LIMPIOS guardados exitosamente en la base de datos!');
        } else {
          throw new Error(response.message || 'Error al guardar en base de datos');
        }
        
      } catch (error) {
        addLogMessage(`❌ Error al guardar: ${error.message}`);
        alert(`Error al guardar en base de datos: ${error.message}`);
        console.error('Error en saveToDatabase:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    const exportToCSV = async () => {
      try {
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `concatenado_limpio_${timestamp}.csv`;
        const totalRecs = cleanedData.totalRecords || cleanedData.data.length;
        
        addLogMessage(`📥 Preparando descarga de datos LIMPIOS (${totalRecs.toLocaleString()} registros estimados)...`);
        
        // OPCIÓN 1: Si tenemos archivos originales, procesarlos en backend con opciones de limpieza
        if (originalFilesForExport && originalFilesForExport.length > 0) {
        setIsProcessing(true);
          addLogMessage(`📤 Enviando ${originalFilesForExport.length} archivos originales al servidor con configuración de limpieza...`);
        
          try {
        // Crear FormData con los archivos originales
        const formData = new FormData();
            originalFilesForExport.forEach((file, index) => {
          console.log(`Agregando archivo ${index + 1}:`, file.name, file.size);
          formData.append('files', file);
          
          // ✅ AGREGAR skip_rows para cada archivo (mismo formato que processFiles)
          const skipRows = skipRowsConfig[file.name] || 0;
          formData.append(`skip_rows_${index}`, skipRows.toString());
          console.log(`  - skip_rows_${index} = ${skipRows}`);
        });
        
            // ✅ Agregar opciones de limpieza al FormData (como parámetros individuales Form)
            formData.append('removeDuplicates', removeDuplicates.toString());
            formData.append('removeGhosts', removeGhosts.toString());
            formData.append('ghostIdColumn', ghostIdColumn || '');
            formData.append('ghostStatusColumn', ghostStatusColumn || '');
            formData.append('ghostStrategy', ghostStrategy || 'latest');
            formData.append('priorityStatus', priorityStatus || '');
            formData.append('dropNaAll', dropNaAll.toString());
            formData.append('dropNaCols', JSON.stringify(dropNaCols));
            formData.append('filterEnabled', filterEnabled.toString());
            formData.append('filterColumn', filterColumn || '');
            formData.append('filterText', filterText || '');
            
            addLogMessage(`⏳ Procesando y limpiando archivos en el servidor (esto puede tardar varios minutos)...`);
            addLogMessage(`🧹 Opciones de limpieza: Duplicados=${removeDuplicates}, Fantasmas=${removeGhosts}`);
            addLogMessage(`📥 La descarga puede tardar 10-30 minutos dependiendo del tamaño final. Por favor espera...`);
            
            // Llamar al endpoint de exportación completa CON limpieza
        const blob = await concatenatorAPI.exportFull(formData);
        
        console.log('Blob recibido:', blob.size, 'bytes');
            addLogMessage(`📦 Archivo limpio recibido del servidor: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        
        // Crear descarga
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
            addLogMessage(`✅ Descarga completa iniciada: ${filename} (datos limpios aplicados)`);
      } catch (error) {
            console.error('Error en exportación completa:', error);
            addLogMessage(`❌ Error al procesar archivos completos: ${error.message}`);
            addLogMessage(`ℹ️ Intentando descarga de muestra limpia disponible...`);
        downloadSampleAsCSV();
      } finally {
        setIsProcessing(false);
          }
        } 
        // OPCIÓN 2: No hay archivos originales, descargar la muestra limpia que ya tenemos
        else {
          addLogMessage(`ℹ️ No hay archivos originales. Descargando muestra limpia de ${cleanedData.data.length.toLocaleString()} registros`);
          downloadSampleAsCSV();
        }
      } catch (error) {
        console.error('Error en exportToCSV:', error);
        addLogMessage(`❌ Error general en exportación: ${error.message}`);
        downloadSampleAsCSV();
      }
    };

    const downloadSampleAsCSV = () => {
      try {
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `concatenado_muestra_${timestamp}.csv`;
        
        if (!cleanedData || !cleanedData.data || cleanedData.data.length === 0) {
          addLogMessage(`❌ No hay datos para exportar`);
          return;
        }
        
        const headers = Object.keys(cleanedData.data[0]);
        const csvContent = [
          headers.join(','),
          ...cleanedData.data.map(row => 
            headers.map(header => {
              const value = row[header];
              if (value === null || value === undefined || value === '') return '';
              const stringValue = String(value);
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }).join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        addLogMessage(`✅ Descarga de muestra iniciada: ${filename} (${cleanedData.data.length.toLocaleString()} registros)`);
      } catch (error) {
        addLogMessage(`❌ Error al descargar muestra: ${error.message}`);
      }
    };

    const exportToExcel = () => {
      try {
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `concatenado_procesado_${timestamp}.csv`; // Usar CSV por simplicidad
        addLogMessage(`📥 Preparando descarga ${filename}...`);
        
        // Por ahora exportar como CSV (Excel requiere librerías adicionales)
        exportToCSV();
        
        addLogMessage(`ℹ️ Nota: El archivo se descarga en formato CSV compatible con Excel`);
      } catch (error) {
        addLogMessage(`❌ Error al descargar: ${error.message}`);
      }
    };

    return (
      <div className="step-content">
        <h3>Paso 3: Visualizar resultados finales y opciones de exportación</h3>
        
        {/* Resumen final */}
        <Card className="section-card">
          <h4>🎉 Procesamiento Completado</h4>
          
          <div className="final-metrics">
            <Metric label="Registros Finales" value={cleanedData.totalRealBD ? `${cleanedData.totalRealBD.toLocaleString()} (BD total)` : cleanedData.totalRecords.toLocaleString()} />
            <Metric label="Muestra Final" value={cleanedData.data.length.toLocaleString()} />
            <Metric label="Columnas" value={cleanedData.columns.toString()} />
            <Metric label="Registros Eliminados" value={cleaningResults?.totalRemoved?.toLocaleString() || '0'} />
            <Metric label="% Eliminado" value={`${cleaningResults?.percentRemoved || 0}%`} />
          </div>
          
          {cleaningResults && (
            <div className="cleaning-summary">
              <h5>🧹 Detalle de Limpieza Aplicada (REAL)</h5>
              <div className="cleaning-stats">
                {cleaningResults.totalRemoved === 0 ? (
                  <div>✅ No se encontraron registros para limpiar - Los datos ya están limpios</div>
                ) : (
                  <>
                {cleaningResults.duplicatesRemoved > 0 && (
                  <div>• Duplicados eliminados: {cleaningResults.duplicatesRemoved.toLocaleString()}</div>
                )}
                {cleaningResults.ghostsRemoved > 0 && (
                  <div>• Registros fantasma eliminados: {cleaningResults.ghostsRemoved.toLocaleString()}</div>
                    )}
                    {cleaningResults.naAllRemoved > 0 && (
                      <div>• Filas con todos valores nulos eliminadas: {cleaningResults.naAllRemoved.toLocaleString()}</div>
                    )}
                    {cleaningResults.naColsRemoved > 0 && (
                      <div>• Filas con valores nulos en columnas específicas eliminadas: {cleaningResults.naColsRemoved.toLocaleString()}</div>
                    )}
                    {cleaningResults.filterRemoved > 0 && (
                      <div>• Filas eliminadas por filtro: {cleaningResults.filterRemoved.toLocaleString()}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Guardar en base de datos */}
        <Card className="section-card">
          <h4>💾 Guardar en Base de Datos</h4>
          
          {selectedMarketplace ? (
            <div className="save-info">
              <div><strong>Marketplace:</strong> {selectedMarketplace.toUpperCase()}</div>
              <div><strong>Operación:</strong> Actualizar concatenado existente</div>
              <div><strong>Registros:</strong> {cleanedData.totalRecords.toLocaleString()}</div>
            </div>
          ) : (
            <div className="warning-message">
              ⚠️ No hay marketplace base seleccionado. Se creará un nuevo concatenado.
            </div>
          )}
          
          <Button
            variant="primary"
            onClick={saveToDatabase}
            disabled={isProcessing}
            loading={isProcessing}
            size="large"
          >
            {isProcessing ? 'Guardando...' : '💾 Guardar en Base de Datos'}
          </Button>
        </Card>

        {/* Exportar archivos */}
        <Card className="section-card">
          <h4>📥 Descargar archivos LIMPIOS</h4>
          
          <div className="export-info">
            <p>
              <strong>🧹 Datos listos para descargar con limpieza aplicada:</strong>
            </p>
            <ul>
              {originalFilesForExport.length > 0 ? (
                <>
                  <li>✅ Descarga completa: Procesa todos los archivos originales ({originalFilesForExport.length} archivos) con la limpieza aplicada</li>
                  <li>⚡ Descarga muestra: Descarga solo los {cleanedData.data.length.toLocaleString()} registros limpios cargados en memoria</li>
                </>
              ) : (
                <li>⚡ Descarga disponible: {cleanedData.data.length.toLocaleString()} registros limpios en memoria</li>
              )}
            </ul>
            <p className="warning-note">
              ⚠️ <strong>Importante:</strong> Si vuelves a subir estos archivos descargados, NO deberían encontrarse duplicados ni registros fantasma (ya fueron eliminados).
            </p>
          </div>
          
          <div className="export-actions">
            <Button variant="secondary" onClick={exportToCSV} disabled={isProcessing}>
              {originalFilesForExport.length > 0 ? '📄 Descargar CSV Completo (Limpio)' : '📄 Descargar CSV Muestra (Limpia)'}
            </Button>
            <Button variant="secondary" onClick={exportToExcel} disabled={isProcessing}>
              📊 Descargar Excel
            </Button>
          </div>
        </Card>

        {/* Vista previa de datos finales */}
        <Card className="section-card">
          <h4>👁️ Vista previa de datos finales</h4>
          
          <div className="data-preview-table">
            {cleanedData.data.slice(0, 10).map((row, index) => (
              <div key={index} className="preview-row">
                <strong>#{index + 1}</strong>
                {Object.entries(row).slice(0, 5).map(([key, value]) => (
                  <span key={key}>
                    <strong>{key}:</strong> {String(value)}
                  </span>
                ))}
              </div>
            ))}
          </div>
          
          <div className="preview-note">
            Mostrando primeros 10 de {cleanedData.totalRecords.toLocaleString()} registros
          </div>
        </Card>

        <div className="completion-message">
          ✅ Módulo Concatenador completado.
        </div>
        
        <div className="step-actions">
          <Button variant="ghost" onClick={resetModule}>
            🔄 Reiniciar módulo Concatenador
          </Button>
        </div>
      </div>
    );
  };

  // ==================== RENDER PRINCIPAL ====================
  
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderStep0();
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep0();
    }
  };

  return (
    <div className="concatenator-module">
      {/* Header principal */}
      <div className="content-header">
        <h1 className="title-primary">Módulo Concatenador – Inyectar Datos a Concatenados Existentes</h1>
        <p className="subtitle">
          Combina datos nuevos con concatenados existentes y aplica limpieza avanzada
        </p>
      </div>

      {/* Indicador de pasos */}
      <div className="steps-indicator">
        {[
          { step: 0, label: 'Cargar Archivos', icon: '📁' },
          { step: 1, label: 'Configurar', icon: '⚙️' },
          { step: 2, label: 'Limpiar', icon: '🧹' },
          { step: 3, label: 'Exportar', icon: '📊' }
        ].map(({ step, label, icon }) => (
          <div 
            key={step}
            className={`step-indicator ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
          >
            <div className="step-number">{icon}</div>
            <div className="step-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Log de progreso */}
      <Card className="progress-log-card">
        <h4>📋 Log de Progreso</h4>
        <div 
          ref={progressLogRef}
          className="progress-log"
        >
          {logMessages.map((message, index) => (
            <div key={index} className="log-message">
              {message}
            </div>
          ))}
        </div>
      </Card>

      {/* Contenido del paso actual */}
      <div className="step-container">
        {renderCurrentStep()}
      </div>

      {/* Navegación entre pasos */}
      <div className="step-navigation">
        {currentStep > 0 && (
          <Button
            variant="secondary"
            onClick={() => setCurrentStep(currentStep - 1)}
            disabled={isProcessing}
          >
            ← Paso anterior
          </Button>
        )}
        
        <div className="step-info">
          Paso {currentStep + 1} de 4
        </div>
        
        {currentStep < 3 && currentStep !== 1 && ( // El paso 1 tiene su propio botón de procesamiento
          <Button
            variant="primary"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={isProcessing || (currentStep === 0 && !baseData && uploadedFiles.length === 0)}
          >
            Siguiente paso →
          </Button>
        )}
      </div>

      {/* Estilos CSS basados en Design System Snowflake */}
      <style jsx>{`
        .concatenator-module {
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px;
          background: #F5F7FA;
          min-height: 100vh;
        }

        .content-header {
          text-align: center;
          margin-bottom: 48px;
          padding: 48px 32px;
          background: #FFFFFF;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .title-primary {
          font-size: 36px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .subtitle {
          font-size: 18px;
          color: #4A5568;
          line-height: 1.6;
        }

        .steps-indicator {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-bottom: 48px;
          padding: 24px;
          background: #FFFFFF;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .step-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 20px 28px;
          border-radius: 12px;
          transition: all 0.2s ease;
          background: #F5F7FA;
          border: 2px solid transparent;
          min-width: 140px;
        }

        .step-indicator.active {
          background: rgba(41, 181, 232, 0.1);
          border: 2px solid #29B5E8;
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(41, 181, 232, 0.2);
        }

        .step-indicator.completed {
          background: rgba(56, 193, 114, 0.1);
          border: 2px solid #38C172;
        }

        .step-number {
          font-size: 32px;
          font-weight: 600;
        }

        .step-label {
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          color: #2D3748;
        }

        .progress-log-card {
          margin-bottom: 32px;
          background: #FFFFFF;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        .progress-log-card h4 {
          background: #F5F7FA;
          padding: 16px 24px;
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1A202C;
          border-bottom: 1px solid #E2E8F0;
        }

        .progress-log {
          max-height: 180px;
          overflow-y: auto;
          background: #FFFFFF;
          padding: 16px 24px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.6;
        }

        .log-message {
          padding: 6px 0;
          color: #4A5568;
          border-bottom: 1px solid #F5F7FA;
        }

        .log-message:last-child {
          border-bottom: none;
        }

        .step-content h3 {
          font-size: 30px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 3px solid #29B5E8;
        }

        .section-card {
          margin-bottom: 32px;
          background: #FFFFFF;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #E2E8F0;
        }

        .section-card h4 {
          font-size: 20px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 24px;
          padding-bottom: 12px;
          border-bottom: 2px solid #F5F7FA;
        }

        .marketplace-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin: 24px 0;
        }

        .marketplace-item {
          border: 2px solid #E2E8F0;
          border-radius: 12px;
          padding: 24px;
          background: #F5F7FA;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .marketplace-item:hover {
          border-color: #29B5E8;
          box-shadow: 0 4px 12px rgba(41, 181, 232, 0.15);
          transform: translateY(-2px);
        }

        .marketplace-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid #E2E8F0;
        }

        .marketplace-header h5 {
          font-size: 18px;
          font-weight: 700;
          color: #29B5E8;
          margin: 0;
        }

        .marketplace-count {
          font-size: 12px;
          color: #2D3748;
          background: #FFFFFF;
          padding: 6px 12px;
          border-radius: 24px;
          border: 1px solid #E2E8F0;
          font-weight: 600;
          white-space: nowrap;
        }

        .marketplace-details {
          font-size: 14px;
          color: #4A5568;
          line-height: 1.8;
        }

        .marketplace-details div {
          margin-bottom: 8px;
        }

        .marketplace-details strong {
          color: #2D3748;
          font-weight: 600;
        }

        .selection-controls {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 2px solid #F5F7FA;
        }

        .marketplace-select {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          background: #FFFFFF;
          color: #2D3748;
          font-size: 16px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .marketplace-select:focus {
          outline: none;
          border-color: #29B5E8;
          box-shadow: 0 0 0 3px rgba(41, 181, 232, 0.1);
        }

        .loaded-base-info {
          margin-top: 24px;
          padding: 24px;
          background: rgba(56, 193, 114, 0.08);
          border: 2px solid #38C172;
          border-radius: 12px;
        }

        .loaded-base-info h5 {
          font-size: 18px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 20px;
        }

        .base-metrics {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .preview-details {
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          margin-top: 16px;
          background: #FFFFFF;
        }

        .preview-details summary {
          padding: 16px 20px;
          background: #F5F7FA;
          cursor: pointer;
          font-weight: 600;
          color: #29B5E8;
          font-size: 14px;
          border-radius: 6px 6px 0 0;
        }

        .preview-details summary:hover {
          background: rgba(41, 181, 232, 0.08);
        }

        .data-preview {
          padding: 20px;
        }

        .preview-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #E2E8F0;
        }

        .preview-row:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .preview-row strong {
          color: #29B5E8;
          font-weight: 700;
          margin-right: 8px;
        }

        .preview-row span {
          font-size: 13px;
          padding: 6px 12px;
          background: #F5F7FA;
          border-radius: 6px;
          border: 1px solid #E2E8F0;
          color: #2D3748;
        }

        .base-info {
          padding: 16px 20px;
          background: rgba(41, 181, 232, 0.08);
          border: 2px solid #29B5E8;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 15px;
          color: #1A202C;
        }

        .upload-area {
          border: 3px dashed #8B9CB5;
          border-radius: 16px;
          padding: 64px 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 24px 0;
          background: #FFFFFF;
        }

        .upload-area:hover {
          border-color: #29B5E8;
          background: rgba(41, 181, 232, 0.03);
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(41, 181, 232, 0.1);
        }

        .upload-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .upload-content p {
          font-size: 16px;
          font-weight: 600;
          color: #2D3748;
          margin: 0;
        }

        .upload-icon {
          font-size: 56px;
          opacity: 0.6;
        }

        .upload-subtitle {
          font-size: 14px;
          color: #8B9CB5;
        }

        .files-list {
          margin-top: 32px;
        }

        .files-list h5 {
          font-size: 18px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 20px;
          border-bottom: 2px solid #E2E8F0;
          padding-bottom: 12px;
        }

        .file-item {
          border: 2px solid #E2E8F0;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          background: #FFFFFF;
          transition: all 0.2s ease;
        }

        .file-item:hover {
          border-color: #29B5E8;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }

        .file-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          gap: 16px;
        }

        .file-info strong {
          font-size: 16px;
          color: #1A202C;
          font-weight: 600;
        }

        .file-info span {
          font-size: 13px;
          color: #8B9CB5;
          margin-left: 12px;
          background: #F5F7FA;
          padding: 4px 10px;
          border-radius: 6px;
        }

        .file-remove {
          color: #FF6B35;
          font-weight: 600;
          padding: 8px 16px !important;
          border-radius: 8px;
          background: transparent;
          transition: all 0.2s ease;
        }

        .file-remove:hover {
          background: rgba(255, 107, 53, 0.1);
          color: #FF6B35;
        }

        .file-preview {
          border-top: 2px solid #F5F7FA;
          padding-top: 16px;
          margin-top: 16px;
        }

        .file-preview summary {
          cursor: pointer;
          font-weight: 600;
          color: #29B5E8;
          font-size: 14px;
          padding: 8px 0;
        }

        .file-preview summary:hover {
          text-decoration: underline;
        }

        .preview-info {
          display: flex;
          gap: 24px;
          margin: 16px 0;
          font-size: 13px;
          color: #4A5568;
          font-weight: 600;
        }

        .preview-info span {
          background: #F5F7FA;
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid #E2E8F0;
        }

        .preview-table {
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          overflow: hidden;
          background: #FFFFFF;
        }

        .preview-headers {
          display: flex;
          background: #F5F7FA;
          font-weight: 700;
          border-bottom: 2px solid #E2E8F0;
        }

        .preview-cell {
          flex: 1;
          min-width: 100px;
          max-width: 150px;
          padding: 10px 12px;
          border-right: 1px solid #E2E8F0;
          font-size: 13px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #2D3748;
        }

        .preview-cell:last-child {
          border-right: none;
        }

        .preview-cell.header {
          background: #F5F7FA;
          font-weight: 700;
          color: #1A202C;
        }

        .preview-cell.more {
          font-style: italic;
          color: #8B9CB5;
          text-align: center;
        }

        .step-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 24px;
          margin-top: 48px;
          padding-top: 32px;
          border-top: 2px solid #E2E8F0;
        }

        .ready-message {
          color: #38C172;
          font-weight: 600;
          font-size: 16px;
          background: rgba(56, 193, 114, 0.1);
          padding: 12px 24px;
          border-radius: 8px;
          border: 2px solid #38C172;
        }

        .warning-message {
          color: #FF6B35;
          font-weight: 600;
          font-size: 16px;
          background: rgba(255, 107, 53, 0.1);
          padding: 12px 24px;
          border-radius: 8px;
          border: 2px solid #FF6B35;
        }

        .info-message {
          color: #29B5E8;
          font-weight: 600;
          font-size: 16px;
          background: rgba(41, 181, 232, 0.1);
          padding: 12px 24px;
          border-radius: 8px;
          border: 2px solid #29B5E8;
          margin-bottom: 24px;
        }

        .data-summary {
          margin-bottom: 20px;
          padding: 20px;
          background: #F5F7FA;
          border-radius: 8px;
          border: 2px solid #E2E8F0;
          font-size: 15px;
          line-height: 1.8;
        }

        .data-summary strong {
          color: #1A202C;
          font-weight: 700;
        }

        .file-summary {
          margin-left: 32px;
          margin-top: 8px;
          font-size: 14px;
          color: #4A5568;
          padding: 8px 0;
        }

        .processing-mode {
          margin-top: 20px;
          padding: 16px 20px;
          background: rgba(41, 181, 232, 0.08);
          border: 2px solid #29B5E8;
          border-radius: 8px;
          font-weight: 600;
          font-size: 15px;
          color: #1A202C;
        }

        .file-config-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .file-config-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: #F5F7FA;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .file-config-item:hover {
          border-color: #29B5E8;
          background: rgba(41, 181, 232, 0.03);
        }

        .skip-rows-control {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .skip-rows-control label {
          font-weight: 600;
          font-size: 14px;
          color: #2D3748;
        }

        .skip-rows-input {
          width: 90px;
          padding: 10px 12px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          text-align: center;
          font-weight: 600;
          font-size: 16px;
          background: #FFFFFF;
          transition: all 0.2s ease;
        }

        .skip-rows-input:focus {
          outline: none;
          border-color: #29B5E8;
          box-shadow: 0 0 0 3px rgba(41, 181, 232, 0.1);
        }

        .advanced-options {
          margin-top: 16px;
        }

        .advanced-options summary {
          cursor: pointer;
          font-weight: 700;
          color: #29B5E8;
          font-size: 16px;
          padding: 16px 20px;
          background: #F5F7FA;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .advanced-options summary:hover {
          background: rgba(41, 181, 232, 0.08);
        }

        .advanced-options[open] summary {
          border-radius: 8px 8px 0 0;
          border-bottom: 2px solid #E2E8F0;
        }

        .option-group {
          margin: 24px 0;
          padding: 20px;
          background: #FFFFFF;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
        }

        .option-group > label:first-child {
          font-weight: 700;
          font-size: 15px;
          color: #1A202C;
          display: block;
          margin-bottom: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          margin-bottom: 12px;
          padding: 12px 16px;
          border-radius: 8px;
          transition: all 0.2s ease;
          font-size: 15px;
          font-weight: 500;
          color: #2D3748;
        }

        .checkbox-label:hover {
          background: #F5F7FA;
        }

        .checkbox-label input[type="checkbox"] {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .checkbox-label.small {
          font-size: 14px;
          margin-bottom: 8px;
          padding: 8px 12px;
        }

        .radio-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 16px;
          padding: 16px;
          background: #F5F7FA;
          border-radius: 8px;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 10px 12px;
          border-radius: 6px;
          transition: all 0.2s ease;
          font-size: 14px;
          color: #2D3748;
        }

        .radio-label:hover {
          background: #FFFFFF;
        }

        .radio-label input[type="radio"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .encoding-select {
          width: 240px;
          padding: 12px 16px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          background: #FFFFFF;
          color: #2D3748;
          font-size: 15px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .encoding-select:focus {
          outline: none;
          border-color: #29B5E8;
          box-shadow: 0 0 0 3px rgba(41, 181, 232, 0.1);
        }

        .data-info {
          font-weight: 600;
          color: #1A202C;
          margin-bottom: 32px;
          padding: 20px 24px;
          background: rgba(41, 181, 232, 0.08);
          border: 2px solid #29B5E8;
          border-radius: 12px;
          font-size: 15px;
          line-height: 1.8;
        }

        .processing-mode-info {
          margin-top: 16px;
          padding: 12px 16px;
          background: #F5F7FA;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-size: 14px;
          color: #4A5568;
        }

        .processing-mode-info strong {
          color: #1A202C;
          font-weight: 700;
        }

        .cleaning-section {
          margin-top: 16px;
        }

        .cleaning-section summary {
          cursor: pointer;
          font-weight: 700;
          color: #29B5E8;
          font-size: 16px;
          padding: 16px 20px;
          background: #F5F7FA;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .cleaning-section summary:hover {
          background: rgba(41, 181, 232, 0.08);
        }

        .cleaning-section[open] summary {
          border-radius: 8px 8px 0 0;
          border-bottom: 2px solid #E2E8F0;
        }

        .ghost-config {
          margin-top: 16px;
          padding: 24px;
          background: #F5F7FA;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
        }

        .ghost-selectors {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }

        .selector-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .selector-group label {
          font-weight: 600;
          font-size: 14px;
          color: #1A202C;
        }

        .ghost-select,
        .priority-select,
        .filter-select {
          padding: 12px 16px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          background: #FFFFFF;
          color: #2D3748;
          font-size: 15px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .ghost-select:focus,
        .priority-select:focus,
        .filter-select:focus {
          outline: none;
          border-color: #29B5E8;
          box-shadow: 0 0 0 3px rgba(41, 181, 232, 0.1);
        }

        .strategy-group,
        .priority-group {
          margin-top: 20px;
        }

        .strategy-group label:first-child,
        .priority-group label:first-child {
          font-weight: 700;
          font-size: 15px;
          color: #1A202C;
          display: block;
          margin-bottom: 12px;
        }

        .columns-multiselect {
          max-height: 180px;
          overflow-y: auto;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          padding: 16px;
          background: #F5F7FA;
        }

        .filter-config {
          margin-top: 16px;
          padding: 24px;
          background: #F5F7FA;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
        }

        .filter-selector,
        .filter-values {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }

        .filter-selector label,
        .filter-values label {
          font-weight: 600;
          font-size: 14px;
          color: #1A202C;
        }

        .filter-text-input {
          padding: 12px 16px;
          border: 2px solid #E2E8F0;
          border-radius: 8px;
          background: #FFFFFF;
          color: #2D3748;
          font-size: 15px;
          transition: all 0.2s ease;
        }

        .filter-text-input:focus {
          outline: none;
          border-color: #29B5E8;
          box-shadow: 0 0 0 3px rgba(41, 181, 232, 0.1);
        }

        .final-metrics {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .cleaning-summary {
          margin-top: 32px;
          padding: 24px;
          background: rgba(56, 193, 114, 0.08);
          border: 2px solid #38C172;
          border-radius: 12px;
        }

        .cleaning-summary h5 {
          font-size: 18px;
          font-weight: 700;
          color: #1A202C;
          margin-bottom: 20px;
        }

        .cleaning-stats {
          line-height: 2;
        }

        .cleaning-stats div {
          margin-bottom: 8px;
          font-size: 15px;
          color: #2D3748;
          font-weight: 500;
        }

        .save-info {
          margin-bottom: 32px;
          padding: 24px;
          background: #F5F7FA;
          border-radius: 12px;
          border: 2px solid #E2E8F0;
          line-height: 2;
        }

        .save-info div {
          margin-bottom: 12px;
          font-size: 15px;
          color: #2D3748;
        }

        .save-info strong {
          color: #1A202C;
          font-weight: 700;
          min-width: 140px;
          display: inline-block;
        }

        .export-info {
          margin-bottom: 24px;
          padding: 20px;
          background: #F5F7FA;
          border-radius: 12px;
          border: 2px solid #E2E8F0;
          line-height: 1.8;
        }

        .export-info p {
          margin-bottom: 12px;
          font-size: 15px;
          color: #2D3748;
        }

        .export-info ul {
          margin: 16px 0;
          padding-left: 24px;
          list-style: none;
        }

        .export-info ul li {
          margin-bottom: 10px;
          font-size: 14px;
          color: #4A5568;
          position: relative;
          padding-left: 8px;
        }

        .warning-note {
          margin-top: 16px;
          padding: 16px;
          background: rgba(255, 107, 53, 0.1);
          border: 2px solid #FF6B35;
          border-radius: 8px;
          font-size: 14px !important;
          color: #1A202C !important;
        }

        .warning-note strong {
          color: #FF6B35;
        }

        .export-actions {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .data-preview-table {
          border: 2px solid #E2E8F0;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 20px;
          background: #FFFFFF;
        }

        .preview-note {
          font-size: 14px;
          color: #8B9CB5;
          text-align: center;
          padding: 16px;
          background: #F5F7FA;
          border-radius: 8px;
          font-weight: 600;
        }

        .completion-message {
          text-align: center;
          font-size: 24px;
          font-weight: 700;
          color: #38C172;
          margin: 48px 0;
          padding: 32px;
          background: rgba(56, 193, 114, 0.1);
          border-radius: 12px;
          border: 2px solid #38C172;
        }

        .step-container {
          background: #FFFFFF;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #E2E8F0;
        }

        .step-navigation {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 48px;
          padding: 24px 32px;
          background: #FFFFFF;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid #E2E8F0;
        }

        .step-info {
          font-weight: 600;
          color: #2D3748;
          font-size: 16px;
          background: #F5F7FA;
          padding: 12px 24px;
          border-radius: 8px;
          border: 2px solid #E2E8F0;
        }

        /* Estilos para botones personalizados */
        button {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        /* Scrollbar personalizado */
        .progress-log::-webkit-scrollbar,
        .columns-multiselect::-webkit-scrollbar {
          width: 8px;
        }

        .progress-log::-webkit-scrollbar-track,
        .columns-multiselect::-webkit-scrollbar-track {
          background: #F5F7FA;
          border-radius: 4px;
        }

        .progress-log::-webkit-scrollbar-thumb,
        .columns-multiselect::-webkit-scrollbar-thumb {
          background: #29B5E8;
          border-radius: 4px;
        }

        .progress-log::-webkit-scrollbar-thumb:hover,
        .columns-multiselect::-webkit-scrollbar-thumb:hover {
          background: #1B96C7;
        }

        /* Mejoras de accesibilidad para inputs */
        input:focus,
        select:focus {
          outline: none;
        }

        /* Estilos para código inline */
        code {
          background: #F5F7FA;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 12px;
          color: #29B5E8;
          font-weight: 600;
          border: 1px solid #E2E8F0;
        }

        /* Media Queries para Responsive */
        @media (max-width: 768px) {
          .concatenator-module {
            padding: 16px;
          }

          .content-header {
            padding: 32px 20px;
          }

          .title-primary {
            font-size: 28px;
          }

          .subtitle {
            font-size: 16px;
          }
          
          .steps-indicator {
            flex-direction: row;
            overflow-x: auto;
            justify-content: flex-start;
            padding: 16px;
          }

          .step-indicator {
            min-width: 100px;
            padding: 16px 20px;
          }

          .step-number {
            font-size: 24px;
          }

          .step-label {
            font-size: 12px;
          }
          
          .marketplace-grid {
            grid-template-columns: 1fr;
          }
          
          .selection-controls {
            flex-direction: column;
          }
          
          .base-metrics {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .ghost-selectors {
            grid-template-columns: 1fr;
          }
          
          .final-metrics {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .export-actions {
            flex-direction: column;
          }

          .export-actions button {
            width: 100%;
          }
          
          .step-navigation {
            flex-direction: column;
            gap: 16px;
            padding: 20px;
          }

          .step-navigation button {
            width: 100%;
          }

          .section-card {
            padding: 20px;
          }

          .step-container {
            padding: 20px;
          }

          .step-content h3 {
            font-size: 24px;
          }
        }
      `}</style>
    </div>
  );
};

// Exportar con el nombre esperado por App.js
export default ConcatenatorModule;
